(() => {
  if (customElements.get('sds-collection-page')) return;

  class SdsCollectionPage extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      const lifecycle = this.lifecycle;
      queueMicrotask(() => {
        if (this.isConnected && !lifecycle.signal.aborted) this.initialize(lifecycle.signal);
      });
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.cancelRequest();
    }

    initialize(signal) {
      this.grid = this.querySelector('[data-sds-product-list]');
      this.counts = Array.from(this.querySelectorAll('[data-sds-product-count]'));
      this.pager = this.querySelector('[data-sds-color-pagination]');
      this.nativePagers = Array.from(this.querySelectorAll('[data-sds-native-pagination]'));
      this.nativeEmpty = Array.from(this.querySelectorAll('[data-sds-native-empty]'));
      this.status = this.querySelector('[data-sds-color-status]');
      this.empty = this.querySelector('[data-sds-color-empty]');
      this.colorButtons = Array.from(this.querySelectorAll('button[data-color-value]'));
      this.featureTemplates = Array.from(this.querySelectorAll('template[data-sds-feature-template]'));
      this.pageSize = Number(this.dataset.pageSize);
      this.nativeURL = new URL(window.location.href);
      this.nativeURL.searchParams.delete('sds_color');
      this.nativeURL.searchParams.delete('section_id');
      this.nativeState = {
        products: Array.from(this.grid?.childNodes || []),
        gridHidden: this.grid?.hidden,
        counts: this.counts.map((node) => node.textContent),
        pagersHidden: this.nativePagers.map((node) => node.hidden),
        nativeEmptyHidden: this.nativeEmpty.map((node) => node.hidden),
        colorPagerChildren: Array.from(this.pager?.childNodes || []),
        colorPagerHidden: this.pager?.hidden,
        emptyHidden: this.empty?.hidden
      };
      this.options = new Map();
      this.cache = new Map();
      this.activeColor = '';
      this.matches = null;
      this.requestVersion = 0;

      for (const button of this.colorButtons) {
        const value = button.dataset.colorValue;
        if (!value) continue;
        const id = button.dataset.colorCollectionId;
        const url = SdsCollectionPage.safeColorURL(value, button.dataset.colorUrl, window.location.href);
        if (url && /^\d+$/.test(id || '')) this.options.set(value, { url, id });
      }

      this.addEventListener('click', (event) => this.onClick(event), { signal });
      document.addEventListener('facet:update', (event) => this.onFacetUpdate(event), { capture: true, signal });
      window.addEventListener('popstate', () => this.onPopstate(), { signal });
      this.syncFacetForms('');
      this.selectButtons('');
      const color = new URL(window.location.href).searchParams.get('sds_color');
      if (color) this.loadColor(color, { historyMode: 'none', page: SdsCollectionPage.readPage(window.location.href) });
    }

    static safeColorURL(value, rawURL, currentURL) {
      if (!value || !rawURL) return null;
      try {
        const current = new URL(currentURL);
        const url = new URL(rawURL, current);
        const path = url.pathname.match(/^(?:\/[a-z]{2,3}(?:-[a-z0-9]{2,8})*)?\/collections\/([^/]+)\/?$/i);
        if (url.origin !== current.origin || !path || decodeURIComponent(path[1]) !== value) return null;
        if (url.username || url.password || !['http:', 'https:'].includes(url.protocol)) return null;
        url.hash = '';
        return url;
      } catch {
        return null;
      }
    }

    static readPage(rawURL) {
      const value = new URL(rawURL).searchParams.get('page');
      return /^\d+$/.test(value || '') && Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : 1;
    }

    static nativeSignature(rawURL) {
      const url = new URL(rawURL);
      ['sds_color', 'page', 'section_id'].forEach((key) => url.searchParams.delete(key));
      url.searchParams.sort();
      return `${url.origin}${url.pathname}?${url.searchParams.toString()}`;
    }

    static intersectProducts(products, collectionId, acceptAll) {
      const matched = [];
      const seen = new Set();
      for (const product of products) {
        const card = product.querySelector('product-card');
        const id = product.dataset.productId || card?.getAttribute('product-id') || card?.getAttribute('handle');
        if (!id) throw new Error('Product identity is missing');
        if (seen.has(id)) continue;
        seen.add(id);
        if (!acceptAll && !product.hasAttribute('data-collection-ids')) throw new Error('Collection membership is missing');
        const ids = (product.dataset.collectionIds || '').split(',').map((value) => value.trim()).filter(Boolean);
        if (acceptAll || ids.includes(String(collectionId))) matched.push(product);
      }
      return matched;
    }

    static paginate(products, requestedPage, pageSize) {
      if (!Number.isSafeInteger(pageSize) || pageSize < 1) throw new Error('Invalid page size');
      const totalPages = Math.max(1, Math.ceil(products.length / pageSize));
      const page = Math.min(totalPages, Math.max(1, Number.isSafeInteger(requestedPage) ? requestedPage : 1));
      return { page, totalPages, products: products.slice((page - 1) * pageSize, page * pageSize) };
    }

    static pageNumbers(totalPages, currentPage) {
      if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
      return [...new Set([1, totalPages, currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2])]
        .filter((page) => page >= 1 && page <= totalPages).sort((left, right) => left - right);
    }

    cancelRequest() {
      this.requestController?.abort();
      this.requestVersion = (this.requestVersion || 0) + 1;
    }

    selectButtons(color) {
      this.colorButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.colorValue === color)));
    }

    syncFacetForms(color) {
      document.querySelectorAll('facets-form').forEach((facetForm) => {
        if (facetForm.getAttribute('section-id') !== this.dataset.sectionId) return;
        const form = facetForm.querySelector('form');
        if (!form) return;
        const existing = Array.from(form.querySelectorAll('input[name="sds_color"]'));
        if (!color) {
          existing.forEach((input) => input.remove());
          return;
        }
        const input = existing.shift() || document.createElement('input');
        input.type = 'hidden';
        input.name = 'sds_color';
        input.value = color;
        if (!input.parentNode) form.append(input);
        existing.forEach((duplicate) => duplicate.remove());
      });
    }

    onFacetUpdate(event) {
      const rawURL = event.detail?.url;
      if (!rawURL) return;
      const url = new URL(rawURL, window.location.href);
      if (url.searchParams.get('section_id') !== this.dataset.sectionId || url.origin !== window.location.origin) return;
      this.cancelRequest();
      this.setLoading(false);
      if (this.activeColor) {
        url.searchParams.set('sds_color', this.activeColor);
        url.searchParams.delete('page');
      } else {
        url.searchParams.delete('sds_color');
      }
      this.syncFacetForms(this.activeColor);
      event.detail.url = url;
    }

    onPopstate() {
      if (SdsCollectionPage.nativeSignature(window.location.href) !== SdsCollectionPage.nativeSignature(this.nativeURL)) {
        this.cancelRequest();
        window.location.reload();
        return;
      }
      const color = new URL(window.location.href).searchParams.get('sds_color');
      if (!color) {
        this.cancelRequest();
        window.location.reload();
        return;
      }
      this.loadColor(color, { historyMode: 'none', page: SdsCollectionPage.readPage(window.location.href) });
    }

    onClick(event) {
      const colorButton = event.target.closest('button[data-color-value]');
      if (colorButton && this.contains(colorButton)) {
        event.preventDefault();
        const details = colorButton.closest('details');
        if (details && this.contains(details)) {
          details.open = false;
          details.querySelector('summary')?.focus({ preventScroll: true });
        }
        const color = colorButton.dataset.colorValue;
        if (!color) {
          if (!this.activeColor && !new URL(window.location.href).searchParams.has('sds_color')) return;
          this.cancelRequest();
          const url = new URL(window.location.href);
          ['sds_color', 'page', 'section_id'].forEach((key) => url.searchParams.delete(key));
          window.location.assign(url.toString());
        } else if (color !== this.activeColor) {
          this.loadColor(color, { historyMode: 'push', page: 1 });
        }
        return;
      }
      const pageButton = event.target.closest('button[data-sds-color-page]');
      if (pageButton && this.contains(pageButton) && this.activeColor && this.matches && !this.loading) {
        event.preventDefault();
        const page = Number(pageButton.dataset.sdsColorPage);
        if (page === this.currentPage || !Number.isSafeInteger(page)) return;
        this.renderPage(page, 'push');
        this.pager?.querySelector(`button[data-sds-color-page="${this.currentPage}"]`)?.focus({ preventScroll: true });
        this.grid?.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }

    sourceURL() {
      const url = new URL(window.location.href);
      ['page', 'sds_color', 'section_id', 'sections'].forEach((key) => url.searchParams.delete(key));
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.hash = '';
      return url;
    }

    writeURL(color, page, mode) {
      if (mode === 'none') return;
      const url = new URL(window.location.href);
      url.searchParams.set('sds_color', color);
      url.searchParams.delete('section_id');
      if (page > 1) url.searchParams.set('page', String(page));
      else url.searchParams.delete('page');
      if (url.toString() === window.location.href) return;
      history[mode === 'push' ? 'pushState' : 'replaceState'](history.state || {}, '', url.toString());
    }

    async loadColor(color, { historyMode = 'push', page = 1 } = {}) {
      this.cancelRequest();
      const version = this.requestVersion;
      const controller = new AbortController();
      this.requestController = controller;
      const option = this.options.get(color);
      if (!option || !this.grid || !this.pager || !Number.isSafeInteger(this.pageSize) || this.pageSize < 1 || (!/^\d+$/.test(this.dataset.collectionId || '') && this.dataset.collectionHandle !== 'all')) {
        this.restoreNative(true);
        return;
      }
      this.activeColor = color;
      this.matches = null;
      this.selectButtons(color);
      this.syncFacetForms(color);
      this.writeURL(color, page, historyMode);
      this.nativePagers.forEach((node) => { node.hidden = true; });
      this.nativeEmpty.forEach((node) => { node.hidden = true; });
      this.pager.hidden = true;
      if (this.empty) this.empty.hidden = true;
      this.setLoading(true);

      try {
        const source = this.sourceURL();
        const key = `${source.toString()}|${option.id}`;
        let matches = this.cache.get(key);
        if (!matches) {
          matches = await this.fetchProducts(source, controller.signal, option.id);
          if (controller.signal.aborted || version !== this.requestVersion || !this.isConnected) return;
          this.cache.set(key, matches);
          if (this.cache.size > 4) this.cache.delete(this.cache.keys().next().value);
        }
        if (controller.signal.aborted || version !== this.requestVersion || !this.isConnected) return;
        this.matches = matches;
        this.setLoading(false);
        this.renderPage(page, 'replace');
        if (this.status) {
          this.status.textContent = matches.length ? this.formatCount(matches.length) : this.status.dataset.emptyText || '';
          this.status.classList.add('sr-only');
        }
      } catch (error) {
        if (controller.signal.aborted || version !== this.requestVersion || !this.isConnected || error.name === 'AbortError') return;
        controller.abort();
        this.restoreNative(true);
      }
    }

    async fetchPage(sourceURL, page, signal) {
      const url = new URL(sourceURL);
      url.searchParams.set('page', String(page));
      const response = await fetch(url.toString(), { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Collection section request failed');
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = Array.from(parsed.querySelectorAll('sds-collection-page')).find((node) => node.dataset.sectionId === this.dataset.sectionId);
      const meta = root?.querySelector('[data-sds-pagination-meta]');
      const grid = root?.querySelector('[data-sds-product-list]');
      const rawPages = meta?.dataset.totalPages;
      if (!root || !grid || !/^\d+$/.test(rawPages || '') || root.dataset.collectionHandle !== this.dataset.collectionHandle) throw new Error('Collection section metadata is missing');
      const totalPages = Math.max(1, Number(rawPages));
      if (!Number.isSafeInteger(totalPages) || totalPages > 1000) throw new Error('Collection pagination cannot be completed');
      const products = Array.from(grid.querySelectorAll('[data-sds-product]'));
      if ((Number(rawPages) === 0 && products.length) || (page > 1 && !products.length)) throw new Error('Collection pagination changed');
      return { totalPages, products };
    }

    async fetchProducts(sourceURL, signal, colorCollectionId) {
      if (!/^\d+$/.test(colorCollectionId || '')) throw new Error('Color collection identity is missing');
      const first = await this.fetchPage(sourceURL, 1, signal);
      const pages = new Array(first.totalPages);
      pages[0] = first.products;
      let nextPage = 2;
      const workers = Array.from({ length: Math.min(4, first.totalPages - 1) }, async () => {
        while (nextPage <= first.totalPages) {
          if (signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
          const page = nextPage++;
          const result = await this.fetchPage(sourceURL, page, signal);
          if (result.totalPages !== first.totalPages) throw new Error('Collection pagination changed');
          pages[page - 1] = result.products;
        }
      });
      await Promise.all(workers);
      return SdsCollectionPage.intersectProducts(pages.flat(), colorCollectionId, false);
    }

    formatCount(count) {
      let number;
      try {
        number = new Intl.NumberFormat(document.documentElement.lang || undefined).format(count);
      } catch {
        number = String(count);
      }
      const template = this.dataset.countTemplate || '{{count}}';
      return template.replace(/__count__|\{\{\s*count\s*\}\}/g, number);
    }

    renderPage(requestedPage, historyMode) {
      const result = SdsCollectionPage.paginate(this.matches, requestedPage, this.pageSize);
      this.currentPage = result.page;
      const content = [];
      const pageOffset = (result.page - 1) * this.pageSize;
      result.products.forEach((product, index) => {
        content.push(document.importNode(product, true));
        const productIndex = pageOffset + index + 1;
        this.featureTemplates.forEach((template) => {
          if (Number(template.dataset.afterProducts) !== productIndex) return;
          const feature = template.content.querySelector('[data-sds-feature]');
          if (feature) content.push(document.importNode(feature, true));
        });
      });
      this.grid.replaceChildren(...content);
      this.grid.hidden = this.matches.length === 0;
      this.counts.forEach((node) => { node.textContent = this.formatCount(this.matches.length); });
      if (this.empty) this.empty.hidden = this.matches.length !== 0;
      this.renderPagination(result.totalPages, result.page);
      this.writeURL(this.activeColor, result.page, historyMode);
    }

    renderPagination(totalPages, currentPage) {
      const fragment = document.createDocumentFragment();
      let previous = 0;
      for (const page of SdsCollectionPage.pageNumbers(totalPages, currentPage)) {
        if (previous && page > previous + 1) {
          const gap = document.createElement('span');
          gap.className = 'pagination__link';
          gap.textContent = '…';
          gap.setAttribute('aria-hidden', 'true');
          fragment.append(gap);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pagination__link h6';
        button.dataset.sdsColorPage = String(page);
        button.textContent = String(page);
        const label = this.pager.dataset.pageLabelTemplate;
        if (label) button.setAttribute('aria-label', label.replace(/__page__|\{\{\s*page\s*\}\}/g, String(page)));
        if (page === currentPage) button.setAttribute('aria-current', 'page');
        fragment.append(button);
        previous = page;
      }
      this.pager.replaceChildren(fragment);
      this.pager.hidden = totalPages <= 1;
    }

    setLoading(loading) {
      this.loading = loading;
      this.setAttribute('aria-busy', String(loading));
      if (this.grid) this.grid.setAttribute('aria-busy', String(loading));
      if (this.status) {
        this.status.classList.remove('sr-only');
        this.status.textContent = loading ? this.status.dataset.loadingText || '' : '';
      }
    }

    restoreNative(showError = false) {
      this.cancelRequest();
      this.activeColor = '';
      this.matches = null;
      this.setLoading(false);
      this.selectButtons('');
      this.syncFacetForms('');
      if (this.grid) {
        this.grid.replaceChildren(...this.nativeState.products);
        this.grid.hidden = this.nativeState.gridHidden;
      }
      this.counts.forEach((node, index) => { node.textContent = this.nativeState.counts[index]; });
      this.nativePagers.forEach((node, index) => { node.hidden = this.nativeState.pagersHidden[index]; });
      this.nativeEmpty.forEach((node, index) => { node.hidden = this.nativeState.nativeEmptyHidden[index]; });
      if (this.pager) {
        this.pager.replaceChildren(...this.nativeState.colorPagerChildren);
        this.pager.hidden = this.nativeState.colorPagerHidden;
      }
      if (this.empty) this.empty.hidden = this.nativeState.emptyHidden;
      history.replaceState(history.state || {}, '', this.nativeURL.toString());
      if (showError && this.status) this.status.textContent = this.status.dataset.errorText || '';
    }
  }

  customElements.define('sds-collection-page', SdsCollectionPage);
})();
